import {Stack} from '@commercial-helios/ui/src/components/stack'
import {tokens} from '@commercial-helios/ui/theme'
import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import FeatureBlock from '@/components/feature-block'
import Footer from '@/components/footer'
import Header from '@/components/header'
import SEO from '@/components/seo'
import type {CustomerCaseQuery} from '@/graphql-types'
import {SliceZone} from '@/slices/slice-zone'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import Image from '@helios/shared/components/image'
import RichText, {hasRichText} from '@helios/shared/components/rich-text'
import {Main} from '@helios/shared/components/skip-to-content'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {Section} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'
import {isEmptyObject} from '@helios/shared/utils/is-empty-object'
import mq from '@helios/shared/utils/media-query'

type Props = PageProps<CustomerCaseQuery>

const CustomerCaseTemplate = (props: Props) => {
    const slices = props.data.allPrismicTemplateCustomerCase.nodes[0]?.data
    const pageData = props.data.allPrismicCustomerCase.nodes[0]?.data
    const {employees, industry, location, show_more} = useGlobalStrings()

    if (!pageData) {
        return handleNoPrismicData()
    }

    const shouldReplaceCardGrid = !isEmptyObject(pageData.related_customer_cases)
    const shouldReplaceMediaItemGrid = !isEmptyObject(pageData.media_item_grid)

    slices?.body.forEach((slice, index) => {
        if (
            shouldReplaceCardGrid &&
            slice.__typename === 'PrismicTemplateCustomerCaseDataBodyCardGrid'
        ) {
            slices.body[index] = {
                ...slice,
                items: pageData.related_customer_cases.map((item) => ({
                    ...item,
                    ctaLink: item.cta_link,
                    title: item.case_title,
                    tag_text: '',
                    tag_color: '',
                    filterKeywords: '',
                })),
                primary: {
                    ...slice.primary,
                    show_more_text: show_more,
                    blockTitle: {richText: pageData.related_customer_cases_title},
                },
            }
        } else if (
            shouldReplaceMediaItemGrid &&
            slice.__typename === 'PrismicTemplateCustomerCaseDataBodyMediaItemGrid'
        ) {
            slices.body[index] = {
                ...slice,
                items: pageData.media_item_grid.map((item) => ({
                    ...item,
                    item_title: item.title,
                    item_description: item.body,
                    image: item.icon,
                })),
                primary: {
                    ...slice.primary,
                    title: pageData.media_item_grid_title,
                },
            }
        }
    })

    return (
        <>
            <Header />

            <Main>
                <Section>
                    <FeatureBlock
                        titleVariant="heading-large"
                        videoId={pageData.hero_section[0].wistia_video_id}
                        preTitle={pageData.hero_section[0].pre_title}
                        title={pageData.hero_section[0].title}
                        image={pageData.hero_section[0].image}
                    />
                </Section>

                <Section>
                    <ContentWrapper>
                        <CustomerCard>
                            {pageData.customer_card[0].logo && (
                                <Image width={290} image={pageData.customer_card[0].logo} />
                            )}
                            <CardDetails>
                                <CardDetail>
                                    <Text as="h6" variant="label-base-medium" color="brand">
                                        {industry}
                                    </Text>
                                    <Text variant="paragraph-base">
                                        {pageData.customer_card[0].industry}
                                    </Text>
                                </CardDetail>

                                <CardDetail>
                                    <Text as="h6" variant="label-base-medium" color="brand">
                                        {location}
                                    </Text>
                                    <Text variant="paragraph-base">
                                        {pageData.customer_card[0].location}
                                    </Text>
                                </CardDetail>

                                <CardDetail>
                                    <Text as="h6" variant="label-base-medium" color="brand">
                                        {employees}
                                    </Text>
                                    <Text variant="paragraph-base">
                                        {pageData.customer_card[0].employees}
                                    </Text>
                                </CardDetail>
                            </CardDetails>

                            {hasRichText(pageData.customer_card[0].quote) && (
                                <RichText
                                    variant="paragraph-base"
                                    withQuotes
                                    content={pageData.customer_card[0].quote}
                                />
                            )}
                            {pageData.customer_card[0].quote_author && (
                                <QuoteAuthor>
                                    {pageData.customer_card[0].quote_author_image && (
                                        <Image
                                            width={48}
                                            image={pageData.customer_card[0].quote_author_image}
                                        />
                                    )}
                                    <Text variant="label-base">
                                        {pageData.customer_card[0].quote_author}
                                    </Text>
                                </QuoteAuthor>
                            )}
                        </CustomerCard>
                        <ContentLayout>
                            <ContentSection>
                                <Text as="h2" variant="heading-base">
                                    {pageData.challenge_title}
                                </Text>
                                {hasRichText(pageData.challenge_lead_text.richText) && (
                                    <RichText content={pageData.challenge_lead_text.richText} />
                                )}

                                <StyledList as="ul" space={20}>
                                    {pageData.challenge_list.map((listItem) => (
                                        <li>
                                            <LiContent>
                                                <Text variant="paragraph-large">
                                                    {listItem.challenge_list_item_title}
                                                </Text>
                                                {hasRichText(
                                                    listItem.challenge_list_item_body.richText
                                                ) && (
                                                    <RichText
                                                        variant="paragraph-base"
                                                        content={
                                                            listItem.challenge_list_item_body
                                                                .richText
                                                        }
                                                    />
                                                )}
                                            </LiContent>
                                        </li>
                                    ))}
                                </StyledList>
                            </ContentSection>

                            <ContentSection>
                                <Text as="h2" variant="heading-base">
                                    {pageData.solution_title}
                                </Text>
                                {hasRichText(pageData.solution_lead_text.richText) && (
                                    <RichText content={pageData.solution_lead_text.richText} />
                                )}
                            </ContentSection>

                            <ContentSection>
                                <Text as="h2" variant="heading-base">
                                    {pageData.result_title}
                                </Text>
                                {hasRichText(pageData.result_lead_text.richText) && (
                                    <RichText content={pageData.result_lead_text.richText} />
                                )}
                            </ContentSection>

                            <StyledList as="ul" space={20}>
                                {pageData.result_list.map((listItem) => (
                                    <li>
                                        <LiContent>
                                            <Text variant="paragraph-large">
                                                {listItem.result_list_item_title}
                                            </Text>
                                            {hasRichText(
                                                listItem.result_list_item_body.richText
                                            ) && (
                                                <RichText
                                                    variant="paragraph-base"
                                                    content={
                                                        listItem.result_list_item_body.richText
                                                    }
                                                />
                                            )}
                                        </LiContent>
                                    </li>
                                ))}
                            </StyledList>
                        </ContentLayout>
                    </ContentWrapper>
                </Section>

                {slices?.body && <SliceZone slices={slices.body} />}
            </Main>

            <Footer />
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<CustomerCaseQuery>) => {
    const pageData = data.allPrismicTemplateCustomerCase.nodes[0].data
    const customerCaseData = data.allPrismicCustomerCase.nodes[0].data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={customerCaseData.meta_title}
            description={customerCaseData.meta_description || pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image.url}
        />
    )
}

const ContentWrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing48};
    align-items: start;

    ${mq.min('md')} {
        gap: 100px;
        grid-template-columns: 1fr auto;
    }

    ${mq.min('lg')} {
        gap: 190px;
    }
`

const ContentLayout = styled.div`
    display: grid;
    gap: ${tokens.spacing48};
`

const CustomerCard = styled.aside`
    display: grid;
    max-width: 376px;
    padding: ${tokens.spacing20};
    background: ${tokens.shade200};
    border-radius: ${tokens.arc20};
    gap: ${tokens.spacing32};
    justify-items: center;
    justify-self: center;

    ${mq.min('sm')} {
        position: sticky;
        top: 25vh;
        grid-column-start: 2;
        grid-row-start: 1;
    }
`

const CardDetails = styled.div`
    display: grid;
    width: 100%;
    gap: ${tokens.spacing20};
`

const QuoteAuthor = styled.blockquote`
    display: grid;
    grid-template-columns: 48px auto;
    align-items: center;
    gap: ${tokens.spacing20};
    width: 100%;
`
const CardDetail = styled.div`
    display: grid;
    width: 100%;
    gap: ${tokens.spacing4};
`

const ContentSection = styled.div`
    display: grid;
    gap: ${tokens.spacing32};
`

const LiContent = styled.div`
    display: grid;
    gap: ${tokens.spacing12};
`

const StyledList = styled(Stack)`
    li {
        margin-left: ${tokens.spacing40};
        list-style: disc;

        ::marker {
            font-size: ${tokens.font3XLarge};
        }
    }
`

export const query = graphql`
    query CustomerCase($uid: String!, $locale: String!) {
        allPrismicCustomerCase(filter: {uid: {eq: $uid}, lang: {eq: $locale}}) {
            nodes {
                _previewable
                data {
                    meta_title
                    meta_description
                    hero_section {
                        pre_title
                        title
                        wistia_video_id
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                    }
                    customer_card {
                        logo {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        quote_author_image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        location
                        industry
                        employees
                        quote
                        quote_author
                    }
                    challenge_title
                    challenge_lead_text {
                        richText
                    }
                    challenge_list {
                        challenge_list_item_title
                        challenge_list_item_body {
                            richText
                        }
                    }
                    result_title
                    result_lead_text {
                        richText
                    }
                    result_list {
                        result_list_item_title
                        result_list_item_body {
                            richText
                        }
                    }
                    solution_title
                    solution_lead_text {
                        richText
                    }
                    media_item_grid_title
                    media_item_grid {
                        title
                        body {
                            richText
                        }
                        icon {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                    }
                    related_customer_cases_title
                    related_customer_cases_cta_text
                    related_customer_cases {
                        body
                        case_title
                        cta_link {
                            ...PrismicLink
                        }
                        image {
                            dimensions {
                                height
                                width
                            }
                            alt
                            copyright
                            url
                            gatsbyImageData(
                                breakpoints: [60, 80, 120, 180]
                                sizes: "(max-width: 767px) 100vw, (max-width: 991px) 40vw, 55vw"
                            )
                        }
                    }
                }
            }
        }

        allPrismicTemplateCustomerCase(filter: {lang: {eq: $locale}}) {
            nodes {
                _previewable
                data {
                    meta_description
                    open_graph_image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    body {
                        ...CustomerCaseMediaItemGrid
                        ...CustomerCaseGetStarted
                        ...CustomerCaseCardGrid
                    }
                }
            }
        }
    }

    fragment CustomerCaseMediaItemGrid on PrismicTemplateCustomerCaseDataBodyMediaItemGrid {
        __typename
        primary {
            title
            layout
            background
        }
        items {
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            item_title
            item_description {
                richText
            }
        }
    }

    fragment CustomerCaseGetStarted on PrismicTemplateCustomerCaseDataBodyGetStarted {
        __typename
        primary {
            title
            error_message
            button_label
            input_placeholder
            form_subtitle
            aria_label
            hubspot_form_id_production
            hubspot_form_id_staging
            image {
                alt
                copyright
                url
                dimensions {
                    width
                    height
                }
            }
            predefined_signup_banner {
                document {
                    ...SignupBanner
                }
            }
        }
    }

    fragment CustomerCaseCardGrid on PrismicTemplateCustomerCaseDataBodyCardGrid {
        __typename
        primary {
            blockTitle: title {
                richText
            }
            subtitle
            show_more_text
            initial_cards_shown
            initial_cards_shown_on_mobile
            initial_cards_shown_on_desktop
            card_cta_text
            show_filter
            layout
        }
        items {
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            title
            body
            ctaLink: cta_link {
                ...PrismicLink
            }
            filterKeywords: filter_keywords
            tag_text
            tag_color
        }
    }
`

export default withPrismicPreview(CustomerCaseTemplate)
